/**
 * fetchXacThuc — fetch() có xử lý PHIÊN ĐĂNG NHẬP, giống interceptor axios của APIs (_installAuthInterceptor).
 *
 * VÌ SAO CẦN: interceptor chỉ bọc các request đi qua axios (context.apis.*). Component gọi thẳng fetch()
 * (FlowEditor, Quản lý flow, Lịch sử chạy…) thì khi access token (JWT ~15 phút) hết hạn chỉ nhận về 401
 * "Token đã hết hạn, hãy làm mới phiên" và in nguyên câu đó ra màn hình — không làm mới token, cũng không mở
 * form đăng nhập.
 *
 * Gặp 401:
 *   - auth_code = 'token_expired' -> làm mới token qua apis.asyncRefreshToken() (gộp lời gọi: nhiều request
 *     cùng hết hạn chỉ refresh MỘT lần) rồi CHẠY LẠI request đúng một lần bằng token mới. Token của phiên đã
 *     được làm mới ở chỗ khác (props còn cầm token cũ) thì chạy lại luôn bằng token hiện tại, không refresh nữa.
 *     Refresh hỏng / chạy lại vẫn hết hạn -> phiên kết thúc: apis._emitSessionExpired -> Provider mở form đăng nhập.
 *   - Request không mang token (URL không có access_token, không có header token) -> trả nguyên, không đụng phiên.
 *   - auth_code thuộc nhóm phiên chết (thu hồi, token dùng một lần, sai token, tài khoản khoá) VÀ request dùng
 *     đúng token của phiên hiện tại -> mở form đăng nhập. Request bằng public_token / PAT hỏng thì không đá người
 *     dùng ra khỏi phiên đang tốt.
 * Response trả về có cờ `phienKetThuc = true` khi đã chuyển sang đăng nhập lại: nơi gọi KHÔNG hiện lỗi nữa
 * (xem loiPhien bên dưới).
 *
 * @param {object} apis   context.apis (APIs của flexbiz-api); thiếu thì chạy như fetch() thường.
 * @param {string} url
 * @param {object} [init] tham số của fetch()
 * @returns {Promise<Response>}
 */
const PHIEN_CHET = ['session_revoked', 'token_used', 'invalid_token', 'account_disabled'];

const tokenTrongUrl = (url) => {
  const m = /[?&]access_token=([^&#]*)/.exec(String(url || ''));
  if (!m) return '';
  try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
};
const thayTokenUrl = (url, token) => String(url).replace(/access_token=[^&#]*/g, `access_token=${encodeURIComponent(token)}`);
// Header mang token: 'access-token' / 'Access-Token' / 'x-access-token' / 'Authorization: Bearer …' — tên header
// KHÔNG phân biệt hoa thường (AuditModule, StorageSettings gửi 'Access-Token'); headers có thể là object thường
// hoặc đối tượng Headers.
const TEN_HEADER_TOKEN = ['access-token', 'x-access-token'];
const cacHeader = (h) => {
  if (!h) return [];
  if (typeof h.forEach === 'function' && typeof h.get === 'function') { const ra = []; h.forEach((v, k) => ra.push([k, v])); return ra; }
  return Object.entries(h);
};
const tokenTrongHeader = (h) => {
  for (const [k, v] of cacHeader(h)) {
    const t = String(k).toLowerCase();
    if (TEN_HEADER_TOKEN.includes(t) && v) return String(v);
    if (t === 'authorization' && /^Bearer /i.test(String(v || ''))) return String(v).slice(7);
  }
  return '';
};
const thayTokenHeader = (h, token) => {
  if (!h) return h;
  const ra = {};
  for (const [k, v] of cacHeader(h)) {
    const t = String(k).toLowerCase();
    if (TEN_HEADER_TOKEN.includes(t)) ra[k] = token;
    else if (t === 'authorization' && /^Bearer /i.test(String(v || ''))) ra[k] = `Bearer ${token}`;
    else ra[k] = v;
  }
  return ra;
};

async function doc401(res) {
  try {
    const b = await res.clone().json();
    return { authCode: (b && b.auth_code) || '', msg: String((b && (b.error || b.message)) || '') };
  } catch (e) { return { authCode: '', msg: '' }; }
}

const CAU_HET_PHIEN = 'Phiên đăng nhập đã hết — vui lòng đăng nhập lại.';

/**
 * Phiên kết thúc: báo Provider mở form đăng nhập, và trả về một 401 mang câu báo DỄ HIỂU thay cho thông điệp kỹ
 * thuật của máy chủ ("Token đã hết hạn, hãy làm mới phiên") — component cũ chỉ in `error`/`message` của body ra
 * màn hình cũng không còn hiện câu đó. Cờ `phienKetThuc` để component mới bỏ hẳn hộp lỗi.
 */
function ketThucPhien(apis, lyDo, res) {
  try { if (apis && typeof apis._emitSessionExpired === 'function') apis._emitSessionExpired(lyDo); } catch (e) { /* bỏ qua */ }
  let ra = res;
  try {
    if (typeof Response === 'function') {
      ra = new Response(JSON.stringify({ error: CAU_HET_PHIEN, message: CAU_HET_PHIEN, auth_code: lyDo, code: 401 }),
        { status: 401, headers: { 'Content-Type': 'application/json' } });
    }
  } catch (e) { ra = res; }
  try { ra.phienKetThuc = true; } catch (e) { /* bỏ qua */ }
  return ra;
}

export default async function fetchXacThuc(apis, url, init = {}) {
  const res = await fetch(url, init);
  if (res.status !== 401 || !apis) return res;

  const { authCode, msg } = await doc401(res);
  const hetHan = authCode === 'token_expired' || (!authCode && /hết hạn|expired/i.test(msg));
  const daDung = tokenTrongUrl(url) || tokenTrongHeader(init.headers);
  // Request KHÔNG mang token (tài liệu tĩnh, webhook, máy chủ bên ngoài…) -> 401 đó không phải chuyện phiên.
  if (!daDung) return res;
  const hienTai = typeof apis.getAccessToken === 'function' ? apis.getAccessToken() : '';
  let laPublic = false;
  try { laPublic = !!(hienTai && apis.config && apis.config.get && apis.config.get('public_token') === hienTai); } catch (e) { /* bỏ qua */ }

  if (!hetHan) {
    if (PHIEN_CHET.includes(authCode) && hienTai && !laPublic && daDung === hienTai) return ketThucPhien(apis, authCode, res);
    return res;
  }
  if (laPublic) return res;

  let moi = '';
  if (hienTai && daDung && hienTai !== daDung) {
    moi = hienTai;   // phiên đã được làm mới ở chỗ khác — chỉ là component còn cầm token cũ
  } else {
    if (typeof apis.asyncRefreshToken !== 'function') return ketThucPhien(apis, 'refresh_failed', res);
    try {
      const rs = await apis.asyncRefreshToken();
      moi = (rs && (rs.access_token || rs.token)) || '';
    } catch (e) {
      return ketThucPhien(apis, 'refresh_failed', res);
    }
  }
  if (!moi) return ketThucPhien(apis, 'refresh_failed', res);

  const lai = await fetch(thayTokenUrl(url, moi), { ...init, headers: thayTokenHeader(init.headers, moi) });
  if (lai.status === 401) {
    const l = await doc401(lai);
    if (l.authCode === 'token_expired' || PHIEN_CHET.includes(l.authCode)) return ketThucPhien(apis, l.authCode || 'refresh_failed', lai);
  }
  return lai;
}

/** Lỗi ném ra khi phiên đã kết thúc (form đăng nhập đang mở) — nơi gọi kiểm `e.phienKetThuc` để KHÔNG hiện lỗi. */
export function loiPhien() {
  const e = new Error(CAU_HET_PHIEN);
  e.phienKetThuc = true;
  return e;
}

export { tokenTrongUrl, thayTokenUrl, tokenTrongHeader, thayTokenHeader };
